import { AUTH_ERROR_MESSAGES, AUTH_RENEWED_RETRY_MESSAGE } from './authErrors';

/**
 * Retryability contract for GraphQL API errors.
 *
 * Decisions follow the CFA API error-handling table, keyed on the HTTP
 * response status (from the response header, e.g. `ClientError.response.status`)
 * and the GraphQL `errorType` from the response body. Anything not explicitly
 * marked retryable defaults to non-retryable (the table's
 * "Any / Not listed above" row).
 *
 * NOTE: currently wired in only for the troubleshooting drawer queries so the
 * behavior can be validated before rolling it out globally.
 */

/** Error types safe to retry with backoff (exact match, case sensitive). */
const RETRYABLE_ERROR_TYPES = new Set([
  'Error Try Again',
  'Service Call Failure',
  'ExecutionTimeout',
  'Airship Error',
]);

/** Error types that must never be retried (exact match, case sensitive). */
const NON_RETRYABLE_ERROR_TYPES = new Set([
  'Internal Server Error',
  'Unauthorized',
  'System Token Unauthorized',
  'Invalid Input',
  // Bad Gateway / gateway timeouts surfaced as this type should not be retried
  // immediately.
  'Error Try Again Later',
]);

/** Substrings (case sensitive) that mark a retryable error type. */
const RETRYABLE_ERROR_TYPE_SUBSTRINGS = ['Http:', 'Timeout', 'Throttling'];

/** HTTP statuses retryable when the error type is not listed above. */
const RETRYABLE_STATUSES = new Set([429, 502, 504]);

/** HTTP statuses non-retryable when the error type is not listed above. */
const NON_RETRYABLE_STATUSES = new Set([400, 401, 403]);

/** Max attempts beyond the first for the troubleshooting drawer queries. */
export const TROUBLESHOOTING_MAX_RETRIES = 2;

export interface ErrorClassification {
  httpStatus?: number;
  errorType?: string;
}

/**
 * Returns whether an API error should be automatically retried per the CFA
 * error-handling table. Errors with no HTTP response at all (network
 * failure / offline / blocked) are treated as transient and retryable.
 */
export function isRetryableError({
  httpStatus,
  errorType,
}: ErrorClassification): boolean {
  // Error type is the most specific signal (most rows are HTTP 200 + a type).
  if (errorType) {
    if (NON_RETRYABLE_ERROR_TYPES.has(errorType)) return false;
    if (RETRYABLE_ERROR_TYPES.has(errorType)) return true;
    if (RETRYABLE_ERROR_TYPE_SUBSTRINGS.some(sub => errorType.includes(sub))) {
      return true;
    }
  }

  // Error type not listed above: fall back to the HTTP status.
  if (httpStatus !== undefined) {
    if (RETRYABLE_STATUSES.has(httpStatus)) return true;
    if (NON_RETRYABLE_STATUSES.has(httpStatus)) return false;
  }

  // No HTTP response reached us at all (network failure / offline / blocked
  // request): a real response always carries a numeric status, so undefined
  // status + type means a transient network error -> allow retry. A response
  // that arrived with an unrecognized status/type stays non-retryable (the
  // table's "Any / not listed above" row).
  if (httpStatus === undefined && !errorType) return true;

  return false;
}

type UnknownApiError = {
  message?: string;
  response?: {
    status?: number;
    errors?: Array<{ errorType?: string; message?: string }>;
  };
};

/**
 * Extracts the HTTP status and GraphQL errorType from an unknown rejection,
 * covering both a graphql-request `ClientError` and the `{ response: {...} }`
 * shape produced by `handleRequestError`.
 */
export function getErrorClassification(error: unknown): ErrorClassification {
  const err = (error ?? {}) as UnknownApiError;
  return {
    httpStatus: err.response?.status,
    errorType: err.response?.errors?.[0]?.errorType,
  };
}

/**
 * React Query `retry` predicate for the troubleshooting drawer queries.
 * Preserves the app's auth-renewal retry behavior and otherwise defers to the
 * CFA error-handling table.
 */
export function shouldRetryTroubleshootingQuery(
  failureCount: number,
  error: unknown
): boolean {
  const message = (error as { message?: string })?.message ?? '';

  // Terminal auth errors (missing/expired token -> signing out) never retry.
  if (AUTH_ERROR_MESSAGES.includes(message)) return false;

  // Token renewed after an UnauthorizedException: retry with the fresh token.
  if (message === AUTH_RENEWED_RETRY_MESSAGE) {
    return failureCount < TROUBLESHOOTING_MAX_RETRIES;
  }

  const { httpStatus, errorType } = getErrorClassification(error);

  // Surfaced UnauthorizedException (valid token, server rejected): don't retry.
  if (errorType === 'UnauthorizedException') return false;

  return (
    isRetryableError({ httpStatus, errorType }) &&
    failureCount < TROUBLESHOOTING_MAX_RETRIES
  );
}

/** Exponential backoff with a 30s cap: 1s, 2s, 4s, ... */
export function troubleshootingRetryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 30000);
}
